import React, { useEffect, useState } from "react";
import { WaiversCompensatingFactors } from "../exceptions-and-enquiries/WaiversCompensatingFactors";
import {
  Card,
  Checkbox,
  FormControlLabel,
  Grid,
  ListItemIcon
} from "@mui/material";
import { UserDetailsCard } from "../exceptions-and-enquiries/UserDetailsCard";
import { genFormattedUserDetails } from "../../stores/EvaluationResults/EvaluationResults.action";
import { getLatestWaiverResponse } from "../../utils/utils";
import { StatusComponent } from "../waiver-form/StatusComponent";
// import errorIcon from "../../images/guidelineOutput/browse-copy.svg";
import errorIcon from "../../images/guidelineOutput/browse-copy.svg";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";

const WaiveTestsCard = (props: any) => {
  const {
    data,
    customerDetails,
    classes,
    setFailedTestsData,
    commonCompensatingFactors
  } = props;
  const {
    waiverData,
    representableTestName,
    testName,
    isChecked,
    compensatingFactors
  } = data ?? {};

  const [selectedCompensatingFactors, setselectedCompensatingFactors] =
    useState<any>([]);

  const [waiverLastUpdatedInfo, setWaiverLastUpdatedInfo] = useState<any>({});
  const [waiverState, setWaiverState] = useState<string | null>(null);

  useEffect(() => {
    if (!waiverData) {
      setWaiverLastUpdatedInfo(null);
      setWaiverState(null);
      return;
    }

    // ---------------------------
    // 1️⃣ Get Initial User Details (Created By)
    // ---------------------------

    const partyId = waiverData.createdBy ?? waiverData.created_by;
    const createdDate = waiverData.createdOn ?? waiverData.created_on;

    const initialInfo = genFormattedUserDetails(
      customerDetails,
      partyId,
      createdDate
    );

    if (initialInfo) {
      setWaiverLastUpdatedInfo(initialInfo);
    }

    // ---------------------------
    // 2️⃣ Get Waiver Response User Details (Latest Response)
    // ---------------------------

    const responses = waiverData.waiverResponses;

    if (responses?.length > 0) {
      const latestResponseObj = getLatestWaiverResponse(responses); // expected latest object or array
      const latest = Array.isArray(latestResponseObj)
        ? latestResponseObj[latestResponseObj.length - 1]
        : latestResponseObj;

      // Set state
      setWaiverState(latest?.responseType ?? "Requested");

      // Update user info from response
      const lastInfo = genFormattedUserDetails(
        customerDetails,
        latest?.responseBy,
        latest?.responseDate
      );

      if (lastInfo) {
        setWaiverLastUpdatedInfo(lastInfo);
      }
    } else {
      // No responses present
      setWaiverState("Requested");
    }
  }, [waiverData, customerDetails]);

  useEffect(() => {
    if (compensatingFactors?.length) {
      setselectedCompensatingFactors(compensatingFactors);
    }
  }, [compensatingFactors]);

  const onCompensatingFactorClose = (val: any) => {
    setselectedCompensatingFactors(val);
    setFailedTestsData((previous: any) => {
      const findIndex = previous?.findIndex(
        (p: any) => p?.testName === testName
      );
      if (findIndex !== -1) {
        previous[findIndex] = {
          ...previous[findIndex],
          compensatingFactors: val
        };
      }
      return previous;
    });
  };

  const onChangeHandler = (checked: boolean) => {
    setFailedTestsData((previous: any) => {
      const copy = [...previous];
      const findIndex = copy.findIndex((p: any) => p?.testName === testName);

      if (findIndex !== -1) {
        const oldItem = copy[findIndex];

        copy[findIndex] = {
          ...oldItem,
          isChecked: checked,
          compensatingFactors: checked
            ? [
                ...(oldItem?.compensatingFactors ?? []),
                ...(commonCompensatingFactors ?? [])
              ]
            : oldItem?.compensatingFactors ?? []
        };
      }

      return copy;
    });
  };

  return (
    <Card
      sx={{
        boxShadow: "0px 2px 12px 0px #32325D29",
        padding: "8px"
      }}
    >
      <Grid
        container
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        {/* LEFT SIDE CONTENT */}
        <Grid
          item
          style={{
            display: "flex",
            alignItems: "center",
            marginLeft: "12px",
            padding: "8px"
          }}
        >
          <FormControlLabel
            sx={{ alignItems: "center", paddingTop: "8px" }}
            control={
              <Checkbox
                onChange={(e) => onChangeHandler(e?.target?.checked)}
                checked={isChecked}
              />
            }
            label={representableTestName}
          />

          {waiverData && (
            <UserDetailsCard
              alternateText={``}
              userImg="some img will come here"
              accountNameImg={waiverLastUpdatedInfo.NameInitials}
              userFullName={waiverLastUpdatedInfo.userFullName}
              accountType={waiverLastUpdatedInfo.accountTypeFirstLetter}
              date={waiverLastUpdatedInfo.dateTime}
            />
          )}
        </Grid>

        {/* RIGHT SIDE CONTENT */}
        {waiverState ? (
          <Grid item>
            <StatusComponent
              status={waiverState?.toUpperCase()}
              value={`WAIVER ${waiverState}`}
            />
          </Grid>
        ) : (
          <Grid
            item
            className={classes.failedContainer}
            style={{ marginRight: "10px" }}
          >
            <img
              src={errorIcon}
              alt="Test Failure"
              className={classes.warningIcon}
            />
            <span className={classes.failedTypography}>FAILED</span>
          </Grid>
        )}
      </Grid>

      <div style={{ marginLeft: "8px" }}>
        <WaiversCompensatingFactors
          setselectedCompensatingFactors={setselectedCompensatingFactors}
          selectedCompensatingFactors={selectedCompensatingFactors}
          onCompensatingFactorClose={onCompensatingFactorClose}
          fullWidth={true}
        />
      </div>

      {waiverData?.waiverMitigates?.length ? (
        <div style={{ marginLeft: "10px" }}>
          <Accordion
            elevation={0}
            square
            sx={{
              border: "none",
              boxShadow: "none",
              "&:before": { display: "none" }, // remove MUI default line
              padding: 0
            }}
          >
            <AccordionSummary
              expandIcon={null} // remove default positioning
              sx={{
                padding: 0,
                minHeight: "0 !important",
                maxHeight: "16px",
                "& .MuiAccordionSummary-content": {
                  margin: 0,
                  alignItems: "center",
                  display: "flex",
                  gap: "4px" // spacing between text + icon
                }
              }}
            >
              {/* Your text */}
              <Typography
                component="span"
                sx={{ color: "#5e72e4", fontSize: "12px" }}
              >
                View Mitigating Factors
              </Typography>

              {/* Custom Icon placed right of text */}
              <ExpandMoreIcon sx={{ color: "#5e72e4" }} />
            </AccordionSummary>

            <AccordionDetails
              sx={{
                padding: "8px 0" // remove left/right padding
              }}
            >
              <List
                sx={{ width: "100%", bgcolor: "background.paper", padding: 0 }}
              >
                {waiverData?.waiverMitigates?.map((item: any) => (
                  <ListItem key={item.waiverMitigateId} sx={{ padding: 0 }}>
                    <ListItemText
                      primary={`Mitigating Factor ${item.waiverMitigateId}`}
                      secondary={item.description} // <-- add your secondary text here
                      primaryTypographyProps={{
                        fontSize: "11px",
                        lineHeight: "14px",
                        paddingLeft: "4px",
                        fontWeight: "400",
                        color: "#8897aa"
                      }}
                      secondaryTypographyProps={{
                        fontSize: "10px",
                        lineHeight: "13px",
                        paddingLeft: "4px",
                        paddingTop: "4px"
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
        </div>
      ) : null}
    </Card>
  );
};

export default WaiveTestsCard;
